import { useState } from "react";
import { Box, Truck } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import {
  MAQUINARIO_STATUS_LABELS,
  MAQUINA_TIPOS,
  EQUIPAMENTO_TIPOS,
  ATIVO_TIPO_LABELS,
  type AtivoTipo,
  DOCUMENTO_CATEGORIA_LABELS,
  extractApiError,
  uploadMaquinarioDocumento,
  deleteMaquinarioDocumento,
  type MaquinarioDto,
  type MaquinarioFormValues,
  type MaquinarioStatus,
  type MaquinaDocumentoCategoria,
  type MaquinarioDocumentoDto,
  resolveAtivoTipo,
} from "@/services/locacoes";
import { defaultConsumoLimiteLphForTipo, getMaquinaTipoImagemUrl } from "./maquinaTipoImagens";
import { upperLocacaoText } from "./upperLocacaoText";

const STATUS_OPTIONS: MaquinarioStatus[] = ["disponivel", "locado", "manutencao", "inativo"];
const DOC_CATEGORIAS: MaquinaDocumentoCategoria[] = ["crlv", "nota_fiscal", "manual", "foto", "outros"];

function emptyForm(): MaquinarioFormValues {
  return {
    nome: "",
    codigoInterno: "",
    tipoAtivo: "maquinario",
    tipo: "",
    fabricante: "",
    modelo: "",
    anoFabricacao: "",
    placa: "",
    numeroSerie: "",
    observacoes: "",
    horimetroAtual: 0,
    odometroAtual: 0,
    status: "disponivel",
    consumoLimiteLph: 15,
  };
}

function fromDto(m: MaquinarioDto): MaquinarioFormValues {
  return {
    nome: upperLocacaoText(m.nome),
    codigoInterno: upperLocacaoText(m.codigoInterno),
    tipoAtivo: resolveAtivoTipo(m),
    tipo: m.tipo,
    fabricante: upperLocacaoText(m.fabricante),
    modelo: upperLocacaoText(m.modelo),
    anoFabricacao: m.anoFabricacao != null ? String(m.anoFabricacao) : "",
    placa: upperLocacaoText(m.placa),
    numeroSerie: upperLocacaoText(m.numeroSerie),
    observacoes: upperLocacaoText(m.observacoes),
    horimetroAtual: m.horimetroAtual,
    odometroAtual: m.odometroAtual,
    status: m.status,
    consumoLimiteLph: m.consumoLimiteLph ?? defaultConsumoLimiteLphForTipo(m.tipo),
  };
}

interface Props {
  open: boolean;
  editing: MaquinarioDto | null;
  /** Prefill ao criar (ex.: duplicar). Ignorado quando `editing` está definido. */
  prefill?: MaquinarioDto | null;
  busy?: boolean;
  /** Quando definido, fixa o tipo de ativo e oculta o seletor (abas Máquinas / Equipamentos). */
  lockedTipoAtivo?: AtivoTipo;
  onClose: () => void;
  onSave: (values: MaquinarioFormValues, editingId: string | null) => Promise<void>;
  onDelete?: () => void;
  onMutationComplete?: () => void | Promise<void>;
}

export function ModalMaquinario({
  open,
  editing,
  prefill = null,
  busy,
  lockedTipoAtivo,
  onClose,
  onSave,
  onDelete,
  onMutationComplete,
}: Props) {
  const [values, setValues] = useState<MaquinarioFormValues>(emptyForm());
  const [error, setError] = useState<string | null>(null);
  const [documentos, setDocumentos] = useState<MaquinarioDocumentoDto[]>([]);
  const [docCategoria, setDocCategoria] = useState<MaquinaDocumentoCategoria>("crlv");
  const [docBusy, setDocBusy] = useState(false);
  const [pendingDocDeleteId, setPendingDocDeleteId] = useState<string | null>(null);

  const effectiveTipoAtivo: AtivoTipo = lockedTipoAtivo ?? values.tipoAtivo;
  const sessionKey = editing?.id ?? (prefill ? `dup-${prefill.id}` : "new");

  useModalFormSession(open, sessionKey, () => {
    const base = editing
      ? fromDto(editing)
      : prefill
        ? {
            ...fromDto(prefill),
            status: "disponivel" as const,
            // Código precisa ser único — deixa em branco para o usuário informar o novo.
            codigoInterno: "",
          }
        : emptyForm();
    setValues(
      lockedTipoAtivo
        ? {
            ...base,
            tipoAtivo: lockedTipoAtivo,
            horimetroAtual: lockedTipoAtivo === "equipamento" ? 0 : base.horimetroAtual,
            odometroAtual: lockedTipoAtivo === "equipamento" ? 0 : base.odometroAtual,
          }
        : base,
    );
    setDocumentos(editing?.documentos ?? []);
    setError(null);
  });

  const handleSubmit = async () => {
    setError(null);
    if (!values.nome.trim()) {
      setError(effectiveTipoAtivo === "equipamento" ? "Informe o nome do equipamento." : "Informe o nome do maquinário.");
      return;
    }
    if (!values.codigoInterno.trim()) {
      setError("Informe o código interno.");
      return;
    }
    if (effectiveTipoAtivo === "maquinario" && !values.tipo.trim()) {
      setError("Informe o tipo do maquinário.");
      return;
    }
    if (effectiveTipoAtivo === "equipamento" && !values.tipo.trim()) {
      setError("Informe o tipo do equipamento.");
      return;
    }
    try {
      await onSave(
        {
          ...values,
          tipoAtivo: effectiveTipoAtivo,
        },
        editing?.id ?? null,
      );
      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao salvar maquinário."));
    }
  };

  const handleUploadDoc = async (file: File) => {
    if (!editing?.id) return;
    setDocBusy(true);
    setError(null);
    try {
      const doc = await uploadMaquinarioDocumento(editing.id, docCategoria, file);
      setDocumentos((prev) => [...prev, doc]);
      await onMutationComplete?.();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao enviar documento."));
    } finally {
      setDocBusy(false);
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!editing?.id) return;
    setDocBusy(true);
    try {
      await deleteMaquinarioDocumento(editing.id, docId);
      setDocumentos((prev) => prev.filter((d) => d.id !== docId));
      await onMutationComplete?.();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao excluir documento."));
    } finally {
      setDocBusy(false);
    }
  };

  const requestDeleteDoc = (docId: string) => setPendingDocDeleteId(docId);

  const statusLocked = editing?.status === "locado";
  const tipoAtivo = effectiveTipoAtivo;
  const isEquipamento = tipoAtivo === "equipamento";
  const titleLabel = editing
    ? isEquipamento
      ? "Editar equipamento"
      : "Editar maquinário"
    : prefill
      ? isEquipamento
        ? "Duplicar equipamento"
        : "Duplicar máquina"
      : isEquipamento
        ? "Novo equipamento"
        : "Nova máquina";

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
        {isEquipamento ? (
          <Box className="h-4 w-4 text-brand-600" />
        ) : (
          <Truck className="h-4 w-4 text-brand-600" />
        )}
      </div>
      <span className="font-bold text-slate-900">{titleLabel}</span>
    </div>
  );

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {editing && onDelete && (
              <Button type="button" variant="secondary" onClick={onDelete} disabled={busy}>
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} loading={busy}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Nome *"
            uppercase
            value={values.nome}
            onChange={(e) => setValues((v) => ({ ...v, nome: e.target.value }))}
          />
          <Input
            label="Código interno *"
            uppercase
            value={values.codigoInterno}
            onChange={(e) => setValues((v) => ({ ...v, codigoInterno: e.target.value }))}
          />
          {!lockedTipoAtivo && (
          <Select
            label="Tipo de ativo *"
            value={values.tipoAtivo}
            onChange={(e) => {
              const nextTipo = e.target.value as AtivoTipo;
              setValues((v) => ({
                ...v,
                tipoAtivo: nextTipo,
                tipo: nextTipo === "equipamento" && v.tipoAtivo === "maquinario" ? "" : v.tipo,
                horimetroAtual: nextTipo === "equipamento" ? 0 : v.horimetroAtual,
                odometroAtual: nextTipo === "equipamento" ? 0 : v.odometroAtual,
              }));
            }}
            options={(
              Object.entries(ATIVO_TIPO_LABELS) as Array<[AtivoTipo, string]>
            ).map(([value, label]) => ({ value, label }))}
          />
          )}
          {!isEquipamento && (
          <Select
            label="Categoria *"
            value={values.tipo}
            onChange={(e) => {
              const tipo = e.target.value;
              setValues((v) => ({
                ...v,
                tipo,
                consumoLimiteLph: tipo ? defaultConsumoLimiteLphForTipo(tipo) : v.consumoLimiteLph,
              }));
            }}
            options={[
              { value: "", label: "Selecione…" },
              ...MAQUINA_TIPOS.map((t) => ({ value: t, label: t })),
            ]}
          />
          )}
          {!isEquipamento && values.tipo && (
            <div className="sm:col-span-2 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <img
                src={getMaquinaTipoImagemUrl(values.tipo, "maquinario")}
                alt={values.tipo}
                className="h-20 w-28 rounded-lg border border-slate-200 object-cover bg-white"
                onError={(e) => {
                  e.currentTarget.src = "/locacoes/tipos/maquinario-default.jpg";
                }}
              />
              <div className="min-w-0 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">{values.tipo}</p>
                <p className="text-xs">Imagem representativa do tipo de máquina</p>
              </div>
            </div>
          )}
          {isEquipamento && (
          <Select
            label="Tipo de equipamento *"
            value={values.tipo}
            onChange={(e) => setValues((v) => ({ ...v, tipo: e.target.value }))}
            options={[
              { value: "", label: "Selecione…" },
              ...EQUIPAMENTO_TIPOS.map((t) => ({ value: t, label: t })),
            ]}
          />
          )}
          {isEquipamento && values.tipo && (
            <div className="sm:col-span-2 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <img
                src={getMaquinaTipoImagemUrl(values.tipo, "equipamento")}
                alt={values.tipo}
                className="h-20 w-28 rounded-lg border border-slate-200 object-cover bg-white"
                onError={(e) => {
                  e.currentTarget.src = "/locacoes/tipos/equipamento-default.jpg";
                }}
              />
              <div className="min-w-0 text-sm text-slate-600">
                <p className="font-semibold text-slate-800">{values.tipo}</p>
                <p className="text-xs">Imagem representativa do tipo de equipamento</p>
              </div>
            </div>
          )}
          <Input
            label="Fabricante"
            uppercase
            value={values.fabricante}
            onChange={(e) => setValues((v) => ({ ...v, fabricante: e.target.value }))}
          />
          <Input label="Modelo" uppercase value={values.modelo} onChange={(e) => setValues((v) => ({ ...v, modelo: e.target.value }))} />
          <Input
            label="Ano fabricação"
            type="number"
            value={values.anoFabricacao}
            onChange={(e) => setValues((v) => ({ ...v, anoFabricacao: e.target.value }))}
          />
          <Input label="Placa" uppercase value={values.placa} onChange={(e) => setValues((v) => ({ ...v, placa: e.target.value }))} />
          <Input
            label="Nº série"
            uppercase
            value={values.numeroSerie}
            onChange={(e) => setValues((v) => ({ ...v, numeroSerie: e.target.value }))}
          />
          {!isEquipamento && (
            <>
          <Input
            label="Limite de consumo (L/h)"
            type="number"
            min={0}
            step="0.1"
            value={String(values.consumoLimiteLph ?? 15)}
            onChange={(e) =>
              setValues((v) => ({ ...v, consumoLimiteLph: Math.max(0, Number(e.target.value) || 0) }))
            }
            hint="Alerta no dashboard quando o consumo médio ultrapassar este valor"
          />
          <Input
            label="Horímetro atual"
            type="number"
            min={0}
            step="0.1"
            value={String(values.horimetroAtual)}
            onChange={(e) => setValues((v) => ({ ...v, horimetroAtual: Number(e.target.value) || 0 }))}
          />
          <Input
            label="Odômetro atual (km)"
            type="number"
            min={0}
            step="0.1"
            value={String(values.odometroAtual)}
            onChange={(e) => setValues((v) => ({ ...v, odometroAtual: Number(e.target.value) || 0 }))}
          />
            </>
          )}
          <Select
            label="Status"
            value={values.status}
            disabled={statusLocked}
            onChange={(e) => setValues((v) => ({ ...v, status: e.target.value as MaquinarioStatus }))}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: MAQUINARIO_STATUS_LABELS[s] }))}
          />
        </div>
        {statusLocked && (
          <p className="text-xs text-amber-700">Encerre a locação ativa para alterar o status manualmente.</p>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observações</label>
          <textarea
            value={values.observacoes}
            onChange={(e) => setValues((v) => ({ ...v, observacoes: upperLocacaoText(e.target.value) }))}
            rows={3}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>

        {editing && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
            <p className="text-sm font-semibold text-slate-800">Documentos</p>
            <div className="flex flex-wrap items-end gap-3">
              <Select
                label="Categoria"
                value={docCategoria}
                onChange={(e) => setDocCategoria(e.target.value as MaquinaDocumentoCategoria)}
                options={DOC_CATEGORIAS.map((c) => ({ value: c, label: DOCUMENTO_CATEGORIA_LABELS[c] }))}
              />
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {docBusy ? "Enviando…" : "Anexar arquivo"}
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx"
                  disabled={docBusy || busy}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleUploadDoc(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            {documentos.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum documento anexado.</p>
            ) : (
              <ul className="space-y-2">
                {documentos.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{d.originalName}</p>
                      <p className="text-xs text-slate-500">{DOCUMENTO_CATEGORIA_LABELS[d.categoria]}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={docBusy}
                      onClick={() => requestDeleteDoc(d.id)}
                    >
                      Excluir
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>

    <ConfirmModal
      isOpen={pendingDocDeleteId != null}
      title="Excluir documento"
      message="Excluir este documento?"
      confirmLabel="Excluir"
      onConfirm={async () => {
        if (!pendingDocDeleteId) return;
        const docId = pendingDocDeleteId;
        setPendingDocDeleteId(null);
        await handleDeleteDoc(docId);
      }}
      onCancel={() => setPendingDocDeleteId(null)}
      loading={docBusy}
    />
    </>
  );
}
